import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

import { authenticationAtom, jwtTokensAtom } from './State';
import { jwtAuthService } from './JWTAuthService';
import { configuration } from './Configuration';
import { BRAND } from '@/brand/brand';
import { useCustomerLogo } from '@/hooks/useCustomerLogo';
import { Button } from '@/components/ui/button';
import { entraErrorKey, takePendingEntraSignIn } from './utilities/entraSso';
import { safeRedirectTarget } from './utilities/loginRedirect';
import { AuthHeader, AuthShell, LEAVE_MS, SuccessCheck } from './website/auth/AuthLayout';
import { markAuthEntrance } from './website/auth/authEntrance';

/** As on the login page: how long the check stands before the page fades out. */
const VERIFIED_HOLD_MS = 1100;

/**
 * Where the server sends the browser back after Microsoft: `/auth/sso?code=…` or `?error=…`.
 * Trades the one-time code (plus this tab's nonce) for the ordinary login response — built after
 * MagicLinkPage, so tokens never travel in a URL.
 */
export default function EntraSsoPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [, setAuthentication] = useAtom(authenticationAtom);
  const [, setJwtTokens] = useAtom(jwtTokensAtom);
  const [error, setError] = useState<string | null>(null);
  // Same ending as the password sign-in: check, fade out, the shell builds up.
  const [verified, setVerified] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const { logo, isFallback } = useCustomerLogo();
  // StrictMode runs effects twice; the code works once, so the second run would fail it.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const complete = async () => {
      const pending = takePendingEntraSignIn();
      const code = searchParams.get('code');
      if (!code || !pending) {
        setError(t(entraErrorKey(searchParams.get('error') ?? (code ? 'expired' : null))));
        return;
      }

      try {
        const response = await fetch(`${configuration.serverUrl}/auth/entra/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, nonce: pending.nonce }),
        });
        if (!response.ok) {
          setError(t(entraErrorKey('expired')));
          return;
        }
        const user = await response.json();
        const tokens = { accessToken: user.accessToken, refreshToken: user.refreshToken };
        jwtAuthService.setTokens(tokens);
        setJwtTokens(tokens);
        setAuthentication(user);
        setVerified(true);
        const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
        setTimeout(() => setLeaving(true), reduce ? 0 : VERIFIED_HOLD_MS);
        setTimeout(() => {
          markAuthEntrance();
          navigate(safeRedirectTarget(pending.redirect) || '/chatbot', { replace: true });
        }, reduce ? 300 : VERIFIED_HOLD_MS + LEAVE_MS);
      } catch {
        setError(t('entraSso.failed'));
      }
    };

    complete();
  }, [searchParams, navigate, setAuthentication, setJwtTokens, t]);

  return (
    <AuthShell logoSrc={logo} logoAlt={isFallback ? BRAND.name : ''} logoHidden={verified} leaving={leaving}>
      {error ? (
        <>
          <AuthHeader title={t('entraSso.signInWithMicrosoft')} subtitle={<span className="text-destructive">{error}</span>} />
          <Button type="button" className="h-10 w-full" onClick={() => navigate('/login', { replace: true })}>
            {t('entraSso.backToLogin')}
          </Button>
        </>
      ) : verified ? (
        <SuccessCheck label={t('loginPage.verifiedTitle')} />
      ) : (
        <div role="status" className="flex items-center gap-3 text-sm text-muted-foreground">
          <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
          {t('entraSso.signingIn')}
        </div>
      )}
    </AuthShell>
  );
}
